import { theme, FONT_RUNE } from "./theme";

/**
 * Visual explainer for the Royal/Glorious "four pieces, three overlapping triples" structure.
 * Renders four numbered tiles in a row and three arcs below them, one per progression type,
 * each arc spanning the three tiles that form that progression. The whole point of the diagram
 * is to make it obvious at a glance that the same four pieces can carry three different means
 * — it's the bit of the rules that's hardest to grasp from prose alone.
 */
export function ProgressionDiagram({
  set,
  triples,
  caption,
}: {
  set: number[];
  triples: Array<{ kind: "AP" | "GP" | "HP"; label: string; pieces: number[]; check: string; color: string }>;
  caption?: string;
}) {
  // SVG geometry (in viewBox units; scales responsively via width:100%).
  const tileW = 60;
  const tileH = 48;
  const tileGap = 12;
  const padX = 14;
  const arcAreaH = 84; // vertical room beneath the tiles for the three stacked arcs
  const labelPadL = 56; // room on the left for the AP/GP/HP labels
  const labelPadR = 84; // room on the right for the check formula
  const w = labelPadL + padX * 2 + set.length * tileW + (set.length - 1) * tileGap + labelPadR;
  const h = tileH + arcAreaH + 18; // 18 = top margin for piece labels
  const tilesY = 4;
  const tilesXStart = labelPadL + padX;

  function tileCenterX(i: number): number {
    return tilesXStart + i * (tileW + tileGap) + tileW / 2;
  }

  // Each progression's arc lives in its own horizontal band so they don't overlap visually.
  const arcRowH = arcAreaH / triples.length;

  return (
    <div
      style={{
        margin: "12px 0 8px",
        padding: "10px 8px",
        background: "rgba(13, 10, 7, 0.45)",
        border: `1px solid ${theme.boardEdge}`,
        borderRadius: 6,
      }}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        style={{ display: "block", maxWidth: 520, margin: "0 auto", height: "auto" }}
        role="img"
        aria-label={caption ?? `Progression diagram for {${set.join(", ")}}`}
      >
        {/* Tiles — the 4-piece set, rendered as numbered chips */}
        {set.map((v, i) => {
          const cx = tileCenterX(i);
          return (
            <g key={`tile-${i}`}>
              <rect
                x={cx - tileW / 2}
                y={tilesY}
                width={tileW}
                height={tileH}
                rx={6}
                fill={theme.whiteFill}
                stroke={theme.whiteEdge}
                strokeWidth={1.5}
              />
              <text
                x={cx}
                y={tilesY + tileH / 2 + 8}
                textAnchor="middle"
                fontFamily={FONT_RUNE}
                fontSize={26}
                fill={theme.whiteNumber}
                fontWeight={600}
              >
                {v}
              </text>
            </g>
          );
        })}

        {/* Three progression arcs — one per row, each connecting the three tiles in that triple */}
        {triples.map((t, ti) => {
          const rowTop = tilesY + tileH + 8 + ti * arcRowH;
          const rowMid = rowTop + arcRowH / 2;
          const indicesInTriple = t.pieces.map((v) => set.indexOf(v)).filter((i) => i >= 0);
          if (indicesInTriple.length < 2) return null;
          const xs = indicesInTriple.map(tileCenterX);
          const leftX = xs[0];
          const rightX = xs[xs.length - 1];

          // Draw a single horizontal "bracket" with small downward ticks above each member tile,
          // plus a center label/value annotation. Looks cleaner than per-triple curves and
          // avoids visual collision when three arcs share the same number tiles.
          const tickH = 6;
          const path =
            `M ${leftX} ${rowMid - tickH} ` +
            `L ${leftX} ${rowMid} ` +
            `L ${rightX} ${rowMid} ` +
            `L ${rightX} ${rowMid - tickH}`;
          const intermediateTicks = xs.slice(1, -1).map((x, k) => (
            <line
              key={`tick-${ti}-${k}`}
              x1={x}
              y1={rowMid}
              x2={x}
              y2={rowMid - tickH}
              stroke={t.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
          ));

          return (
            <g key={`arc-${ti}`}>
              {/* Triple bracket */}
              <path d={path} fill="none" stroke={t.color} strokeWidth={2} strokeLinecap="round" />
              {intermediateTicks}

              {/* Left-side type label (e.g. "AP") */}
              <text
                x={labelPadL - 6}
                y={rowMid + 4}
                textAnchor="end"
                fontFamily={FONT_RUNE}
                fontSize={13}
                fontWeight={700}
                fill={t.color}
              >
                {t.kind}
              </text>

              {/* Right-side check annotation (e.g. "diffs 3, 3") */}
              <text
                x={w - labelPadR + 8}
                y={rowMid + 4}
                textAnchor="start"
                fontFamily={FONT_RUNE}
                fontSize={11}
                fill={theme.accentSoft}
              >
                {t.check}
              </text>
            </g>
          );
        })}
      </svg>
      {caption && (
        <div
          style={{
            color: theme.boardLabel,
            fontFamily: FONT_RUNE,
            fontSize: 11,
            textAlign: "center",
            marginTop: 6,
            letterSpacing: 1,
          }}
        >
          {caption}
        </div>
      )}
    </div>
  );
}
